import React, { useState } from 'react';
import { googleLogout, GoogleOAuthProvider, useGoogleLogin } from '@react-oauth/google';
import { App, Card, Flex, Typography, theme } from 'antd';
import { SubscriptionReactivationModal, useReactivatePlan } from '~features/billing';
import { SearchPerformanceAccessState, useSearchPerformanceAccessState } from '~features/SearchPerformance';
import {
  GscSitemapImportModal,
  useCreateGscIntegrationMutation,
  useDeleteGscIntegrationMutation,
  useGetGscIntegrationsStatusQuery,
} from '~features/gsc-integrations';

import ButtonWithRole from '~components/ButtonWithRole';
import { useEvent } from '../events/hooks/useEvent';
import { GSC_DISABLED_ROLES } from './GscIntegrationRoles';
import SearchPerformanceCancelledCard from './SearchPerformanceCancelledCard';
import SearchPerformanceCard from './SearchPerformanceCard';
import SearchPerformanceLockedCard from './SearchPerformanceLockedCard';

const GSC_OAUTH_SCOPE =
  'email https://www.googleapis.com/auth/userinfo.email https://www.googleapis.com/auth/webmasters.readonly';

const getGoogleClientID = (nodeEnv: string | undefined) =>
  nodeEnv === 'development'
    ? '159418071530-8jce97vsdt2kpa9havt33booq7ch7r6p.apps.googleusercontent.com'
    : '573209089028-ajljj8r60pkla1ja0kpcnmuln10vrhth.apps.googleusercontent.com';

function searchPerformanceCardFor(accessState: SearchPerformanceAccessState, onReactivate: () => void) {
  switch (accessState) {
    case 'not-in-plan':
      return <SearchPerformanceLockedCard />;
    case 'cancelled':
      return <SearchPerformanceCancelledCard onReactivate={onReactivate} />;
    case 'available':
      return <SearchPerformanceCard />;
  }
}

function SearchPerformanceIntegrationCard() {
  const accessState = useSearchPerformanceAccessState();
  const { startReactivation, modalProps } = useReactivatePlan('search_performance_card');

  return (
    <>
      {/* Beside the cards, not inside the cancelled one: a successful reactivation
          clears cancelledAt and swaps in the live card, and the modal's success
          view has to outlive that swap. */}
      <SubscriptionReactivationModal {...modalProps} />
      {searchPerformanceCardFor(accessState, startReactivation)}
    </>
  );
}

function SitemapsCard() {
  const { token } = theme.useToken();
  const { message } = App.useApp();
  const { track } = useEvent();
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const { data: gscStatus, isLoading: isStatusLoading } = useGetGscIntegrationsStatusQuery();
  const [createGscIntegration] = useCreateGscIntegrationMutation();
  const [deleteGscIntegration] = useDeleteGscIntegrationMutation();
  const isConnected = gscStatus?.data?.isConnected ?? false;
  const handleGoogleSignInError = () => message.error('Google sign-in failed. Please try again.');

  const connect = useGoogleLogin({
    flow: 'auth-code',
    scope: GSC_OAUTH_SCOPE,
    onSuccess: async (response) => {
      // The mutation invalidates the GscIntegration tag, so this card's status and
      // any cached sitemap list refresh without a full reload (navigate(0) would
      // destroy the modal we open next).
      try {
        await createGscIntegration({ code: response.code }).unwrap();
      } catch {
        message.error('Could not connect Google Search Console. Please try again.');
        return;
      }
      track('Google Search Colsole Integrated');
      setIsImportModalOpen(true);
    },
    onError: handleGoogleSignInError,
  });

  const disconnect = useGoogleLogin({
    flow: 'auth-code',
    scope: GSC_OAUTH_SCOPE,
    onSuccess: async () => {
      try {
        await deleteGscIntegration().unwrap();
      } catch {
        message.error('Could not disconnect Google Search Console. Please try again.');
        return;
      }
      googleLogout();
      track('Google Search Colsole Removed');
    },
    onError: handleGoogleSignInError,
  });

  return (
    <Card
      title="Sitemaps"
      loading={isStatusLoading}
      styles={{ body: { paddingTop: token.padding, paddingBottom: token.padding } }}
    >
      <Flex justify="space-between" align="center" gap={token.marginLG}>
        <Typography.Text>
          Automatically discover and import your sitemaps to reduce and eliminate any cache misses.
        </Typography.Text>
        {isConnected ? (
          <Flex gap={token.marginXS}>
            <ButtonWithRole type="primary" disabledFor={GSC_DISABLED_ROLES} onClick={() => setIsImportModalOpen(true)}>
              Import sitemaps
            </ButtonWithRole>
            <ButtonWithRole danger disabledFor={GSC_DISABLED_ROLES} onClick={() => disconnect()}>
              Disconnect
            </ButtonWithRole>
          </Flex>
        ) : (
          <ButtonWithRole type="primary" disabledFor={GSC_DISABLED_ROLES} onClick={() => connect()}>
            Connect
          </ButtonWithRole>
        )}
      </Flex>
      <GscSitemapImportModal open={isImportModalOpen} onClose={() => setIsImportModalOpen(false)} />
    </Card>
  );
}

function Integrations() {
  const { token } = theme.useToken();

  return (
    <GoogleOAuthProvider clientId={getGoogleClientID(process.env.NODE_ENV)}>
      <Flex vertical gap={token.marginLG}>
        <Flex vertical gap={token.marginXS}>
          <Typography.Title level={2} style={{ margin: 0 }}>
            Integrations
          </Typography.Title>
          <Typography.Text type="secondary">
            Enrich Prerender capabilities through connection with external data sources
          </Typography.Text>
        </Flex>

        <Flex vertical gap={token.margin}>
          <Typography.Title level={4} style={{ margin: 0 }}>
            Google Search Console
          </Typography.Title>
          <SearchPerformanceIntegrationCard />
          <SitemapsCard />
        </Flex>
      </Flex>
    </GoogleOAuthProvider>
  );
}

export default Integrations;
